import { ModalService, SimpleMessageService } from "@allbesmart/web-app-common";
import { CdkDragDrop, moveItemInArray, transferArrayItem, CdkDropList, CdkDrag } from "@angular/cdk/drag-drop";
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from "@angular/core";
import { Subscription } from "rxjs";
import { ORDER_TYPE_CONFIG } from "src/app/configs/order-type-config";
import { OrderType } from "src/app/enums/order-type";
import { OrderDto, PutOrderDto } from "src/app/models/order-dto";
import { OrderService } from "src/app/services/order.service";
import { OrderTenantAssignmentComponent } from "../order-tenant-assignment/order-tenant-assignment.component";
import { OrderTrackingDetailsConfigurationComponent } from "../order-tracking-details-configuration/order-tracking-details-configuration.component";
import { OperationItemComponent } from "../operation-item/operation-item.component";
import { UpperCasePipe } from "@angular/common";

@Component({
    selector: "oaibox-operations-kanban-board",
    templateUrl: "./operations-kanban-board.component.html",
    styleUrl: "./operations-kanban-board.component.css",
    imports: [
        CdkDropList,
        CdkDrag,
        OperationItemComponent,
        UpperCasePipe,
    ],
})
export class OperationsKanbanBoardComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) orders!: OrderDto[];

  protected readonly OrderType = OrderType;
  protected readonly ORDER_TYPES = Object.values(OrderType);
  protected readonly ORDER_TYPE_CONFIG = ORDER_TYPE_CONFIG;

  protected groupedOrders = {} as Record<OrderType, OrderDto[]>;

  protected draggingFromType?: OrderType;

  private updateOrderSubscription?: Subscription;

  constructor(
    private readonly orderService: OrderService,
    private readonly simpleMessageService: SimpleMessageService,
    private readonly modalService: ModalService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["orders"]) {
      for (const type of this.ORDER_TYPES) this.groupedOrders[type] = [];

      for (const order of this.orders) {
        if (order.orderType) this.groupedOrders[order.orderType].push(order);
      }
    }
  }

  protected drop(event: CdkDragDrop<OrderDto[]>, targetType: OrderType): void {
    // Reorder inside the same column
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      return;
    }

    // Move between columns
    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );

    // Keep the moved order's type in sync with the target column
    const movedOrder = event.container.data[event.currentIndex];
    if (movedOrder) this.updateOrderType(movedOrder, targetType);

    this.draggingFromType = undefined;
  }

  protected isAllowedTarget(type: OrderType): boolean {
    if (!this.draggingFromType) return false;

    const fromIdx = this.ORDER_TYPES.indexOf(this.draggingFromType);
    const toIdx = this.ORDER_TYPES.indexOf(type);

    return toIdx === fromIdx - 1 || toIdx === fromIdx + 1;
  }

  protected canEnterDropList = (drag: any): boolean => {
    const movedOrder = drag.data as OrderDto;
    if (movedOrder.orderType !== OrderType.AWAITING_PROCUREMENT) return true;

    if (!movedOrder.bom) return true;
    return movedOrder.bom.every((item) => item.isAvailable);
  };

  private updateOrderType(orderDto: OrderDto, newOrderType: OrderType): void {
    const oldOrderType = orderDto.orderType;
    orderDto.orderType = newOrderType;

    const putOrderDto: PutOrderDto = {
      id: orderDto.id,
      orderType: newOrderType,
      bom: orderDto.bom,
    };

    this.updateOrderSubscription?.unsubscribe();
    this.updateOrderSubscription = this.orderService
      .updateOrder(putOrderDto)
      .subscribe({
        error: () => {
          orderDto.orderType = oldOrderType;
          this.simpleMessageService.showSimpleMessage({
            message: "Failed to update Order!",
            icon: "fa-solid fa-triangle-exclamation",
            color: "danger",
          });
        },
      });

    if (
      oldOrderType === OrderType.INTEGRATING_AND_TESTING &&
      newOrderType === OrderType.PACKING
    ) {
      this.modalService.pushModal({
        component: OrderTenantAssignmentComponent,
        size: "medium",
        inputs: new Map<string, OrderDto>([["order", orderDto]]),
      });
    } else if (
      oldOrderType === OrderType.PACKING &&
      newOrderType === OrderType.IN_TRANSIT
    ) {
      this.modalService.pushModal({
        component: OrderTrackingDetailsConfigurationComponent,
        size: "medium",
        inputs: new Map<string, OrderDto>([["order", orderDto]]),
      });
    }
  }

  ngOnDestroy(): void {
    this.updateOrderSubscription?.unsubscribe();
  }
}
